import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Layout } from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import ProjectsPage from "./pages/ProjectsPage";
import CustomersPage from "./pages/CustomersPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ReportsPage from "./pages/ReportsPage";
import UsersPage from "./pages/UsersPage";
import LeavePage from "./pages/LeavePage";
import TimeAccountPage from "./pages/TimeAccountPage";
import MyTimeAccountPage from "./pages/MyTimeAccountPage";
// TimeAccountPage = Team-Verwaltung (admin/supervisor/accounting)
// MyTimeAccountPage = Eigenes Konto (alle Rollen)
import SettingsPage from "./pages/SettingsPage";
import MileagePage from "./pages/MileagePage";
import BillingPage from "./pages/BillingPage";
import InvoiceDraftPage from "./pages/InvoiceDraftPage";
import DatevExportPage from "./pages/DatevExportPage";
import CalendarPage from "./pages/CalendarPage";
import AuditLogPage from "./pages/AuditLogPage";
import HolidaysPage from "./pages/HolidaysPage";
import VacationAccountsPage from "./pages/VacationAccountsPage";
import ServiceCategoriesPage from "./pages/ServiceCategoriesPage";
import CategoryReportPage from "./pages/CategoryReportPage";
import TeamOverviewPage from "./pages/TeamOverviewPage";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};



function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-tracking"
        element={
          <ProtectedRoute>
            <TimeTrackingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute allowedRoles={["admin", "supervisor", "accounting"]}>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
            <ApprovalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-overview"
        element={
          <ProtectedRoute allowedRoles={["admin", "supervisor", "accounting"]}>
            <TeamOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={["admin", "supervisor", "accounting"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            <LeavePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-account"
        element={
          <ProtectedRoute>
            <MyTimeAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/time-account"
        element={
          <ProtectedRoute allowedRoles={["admin", "supervisor", "accounting"]}>
            <TimeAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mileage"
        element={
          <ProtectedRoute>
            <MileagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={["admin", "accounting", "supervisor"]}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/invoices/:id"
        element={
          <ProtectedRoute allowedRoles={["admin", "accounting", "supervisor"]}>
            <InvoiceDraftPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/datev"
        element={
          <ProtectedRoute allowedRoles={["admin", "accounting", "supervisor"]}>
            <DatevExportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute allowedRoles={["admin", "accounting", "supervisor"]}>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holidays"
        element={
          <ProtectedRoute>
            <HolidaysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vacation-accounts"
        element={
          <ProtectedRoute allowedRoles={["admin", "accounting", "supervisor"]}>
            <VacationAccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-categories"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ServiceCategoriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/category-report"
        element={
          <ProtectedRoute allowedRoles={["admin", "accounting", "supervisor"]}>
            <CategoryReportPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
